"use client";

import { useState, useEffect } from "react";
import { Loader2, FolderOpen, FileText } from "lucide-react";

export interface ResourcePickerResource {
  id: string;
  title: string;
  description?: string;
  category?: string;
  carrier?: string;
  fileUrl?: string;
  scope?: string;
}

export interface ResourcePickerProps {
  onSelect: (resource: ResourcePickerResource) => void;
}

export function ResourcePicker({ onSelect }: ResourcePickerProps) {
  const [search, setSearch] = useState("");
  const [resources, setResources] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [carriers, setCarriers] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCarrier, setSelectedCarrier] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { getSupabaseSession } = await import("@/app/_module/services/auth");
        const { getApiUrl } = await import("@/app/_module/services/config");
        const AgencyTokenMgr = (await import("@/app/_module/services/agencyTokenManager")).default;
        const session = await getSupabaseSession();
        const agencyId = AgencyTokenMgr.getCurrentAgencyId();
        const headers: any = {
          Authorization: `Bearer ${session?.access_token}`,
          "Content-Type": "application/json",
          ...(agencyId ? { "x-agency-id": agencyId } : {}),
        };

        // Fetch resources, categories, and carriers in parallel
        const [resRes, catRes, carrRes] = await Promise.all([
          fetch(getApiUrl("resources?limit=100"), { headers }),
          fetch(getApiUrl("resources/categories"), { headers }).catch(() => null),
          fetch(getApiUrl("agency-carriers"), { headers }).catch(() => null),
        ]);

        if (resRes.ok) {
          const json = await resRes.json();
          const data = json?.data?.data || json?.data?.resources || json?.data || [];
          setResources(Array.isArray(data) ? data : []);
        }
        if (catRes?.ok) {
          const json = await catRes.json();
          const data = json?.data?.data || json?.data || [];
          setCategories(Array.isArray(data) ? data : []);
        }
        if (carrRes?.ok) {
          const json = await carrRes.json();
          // agency-carriers returns [{carrierId, carrierName, carrierSlug, isEnabled}]
          const raw = json?.data?.data || json?.data || [];
          const mapped = (Array.isArray(raw) ? raw : []).map((c: any) => ({
            id: c.carrierId || c.id || c.name,
            name: c.carrierName || c.name || "Unknown",
          }));
          setCarriers(mapped);
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  const searchLower = search.toLowerCase();
  const filtered = resources.filter((r: any) => {
    if (search && !(
      (r.title || "").toLowerCase().includes(searchLower) ||
      (r.description || "").toLowerCase().includes(searchLower) ||
      (r.carrier || "").toLowerCase().includes(searchLower) ||
      (r.category || "").toLowerCase().includes(searchLower)
    )) return false;
    if (selectedCategory && r.category !== selectedCategory && r.categoryId !== selectedCategory) return false;
    if (selectedCarrier && r.carrier !== selectedCarrier) return false;
    return true;
  });

  return (
    <div className="w-96">
      <div className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-700">
        <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-2 flex items-center gap-1.5">
          <FolderOpen className="w-3.5 h-3.5" /> Share a Resource
        </p>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search resources..."
          className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-md px-3 py-1.5 text-sm border-none focus:outline-none focus:ring-1 focus:ring-blue-500/50 mb-2"
          autoFocus
        />
        {/* Filters */}
        <div className="flex gap-2">
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            className="flex-1 min-w-0 bg-zinc-100 dark:bg-zinc-800 rounded-md px-2 py-1 text-xs text-zinc-600 dark:text-zinc-400 border-none focus:outline-none focus:ring-1 focus:ring-blue-500/50 truncate"
          >
            <option value="">All Categories</option>
            {categories.map((c: any) => (
              <option key={c.id} value={c.id || c.name}>{c.name}</option>
            ))}
          </select>
          <select
            value={selectedCarrier}
            onChange={e => setSelectedCarrier(e.target.value)}
            className="flex-1 min-w-0 bg-zinc-100 dark:bg-zinc-800 rounded-md px-2 py-1 text-xs text-zinc-600 dark:text-zinc-400 border-none focus:outline-none focus:ring-1 focus:ring-blue-500/50 truncate"
          >
            <option value="">All Carriers</option>
            {carriers.map((c: any, i: number) => (
              <option key={`carrier-${c.id || c.name || i}`} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-xs text-zinc-400 py-6">
            {search || selectedCategory || selectedCarrier ? "No resources match your filters" : "No resources available"}
          </p>
        ) : (
          filtered.map((r: any) => (
            <button
              key={r.id}
              onClick={() => onSelect({
                id: r.id,
                title: r.title,
                description: r.description,
                category: r.category,
                carrier: r.carrier,
                fileUrl: r.fileUrl || r.file_url,
                scope: r.scope,
              })}
              className="w-full text-left px-3 py-2.5 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors border-b border-zinc-100 dark:border-zinc-800 last:border-none"
            >
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate">{r.title}</p>
                  {r.description && (
                    <p className="text-xs text-zinc-400 dark:text-zinc-500 truncate mt-0.5">{r.description}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {r.category && (
                      <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded">
                        {r.category}
                      </span>
                    )}
                    {r.carrier && (
                      <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded">
                        {r.carrier}
                      </span>
                    )}
                    {r.scope && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        r.scope === 'personal'
                          ? "text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20"
                          : "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20"
                      }`}>
                        {r.scope === 'personal' ? 'Personal' : r.scope === 'agency-only' ? 'Agency' : 'Shared'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
      <div className="px-3 py-1.5 border-t border-zinc-200 dark:border-zinc-700">
        <p className="text-[10px] text-zinc-400">{filtered.length} resource{filtered.length !== 1 ? "s" : ""}</p>
      </div>
    </div>
  );
}
